'use client';
import { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Box, Typography, TextField, Divider, Stack, Switch, Paper, Button, Grid } from '@mui/material';
import dayjs from 'dayjs';
import { getDriverById, getDriverLicenseImage } from '../../../stores/driverStore';
import { getDriverLicenseDetailFile } from '../../../stores/driverLicenseStore';

import InsertPhotoOutlinedIcon from '@mui/icons-material/InsertPhotoOutlined';
import { useLanguage } from '../../../utils/i18n';
import { fleetApiGo } from '../../../api/axiosClient';
import PickerField from '../../../components/PickerField';
import CloudDownload from '@mui/icons-material/CloudDownload';
import DetailDialog from '../../../components/DetailDialog';
import DownloadIcon from '@mui/icons-material/Download';

const readOnlyFieldStyle = {
  '& .MuiOutlinedInput-root': {
    borderRadius: 2,
    minWidth: 400,
    '& fieldset': { borderColor: '#485D9A' },
    '&:hover fieldset': { borderColor: '#485D9A' },
    '&.Mui-focused fieldset': { borderColor: '#485D9A' }
  },
  '& .MuiInputLabel-root.Mui-focused': { color: '#485D9A' }
};
const activeSwitchStyle = {
  '& .MuiSwitch-switchBase.Mui-checked.Mui-disabled': {
    color: 'primary.main'
  },
  '& .MuiSwitch-switchBase.Mui-checked.Mui-disabled + .MuiSwitch-track': {
    backgroundColor: 'primary.main',
    opacity: 0.5
  }
};
export default function MasterDriverDetail({ driver, id, companyOptions = [] }) {
  const [data, setData] = useState(driver ?? null);
  const [licenseUrl, setLicenseUrl] = useState(null);
  const driverId = id ?? data?.id ?? data?.driver_id ?? null;
  const { t } = useLanguage();
  const [detailOpen, setDetailOpen] = useState(false);
  const [detailContent, setDetailContent] = useState({ type: 'photo', front: '', back: '', document: '', label: '' });

  useEffect(() => {
    let mounted = true;
    const fetchDriver = async () => {
      if (!driverId) return;
      try {
        const res = await getDriverById(driverId);
        if (mounted) setData(res);
      } catch (err) {
        console.error('Failed to load driver by id', err);
      }
    };

    if (!driver || !driver.name || driverId) {
      fetchDriver();
    } else {
      setData(driver);
    }

    return () => {
      mounted = false;
    };
  }, [driver, driverId]);

  useEffect(() => {
    let active = true;
    const loadLicense = async () => {
      if (!driverId) return;
      try {
        const arr = await getDriverLicenseImage(driverId);
        if (!arr) return;
        const blob = new Blob([arr], { type: 'application/pdf' });
        const url = URL.createObjectURL(blob);
        if (active) setLicenseUrl(url);
      } catch (err) {
        console.warn('Cannot load driver license file', err);
      }
    };
    loadLicense();
    return () => {
      active = false;
      if (licenseUrl) URL.revokeObjectURL(licenseUrl);
    };
  }, [driverId, licenseUrl]);
  const getOptionId = (o) => (o == null ? o : (o.value ?? o.id ?? o));
  const getOptionLabel = (o) => (o == null ? '' : (o.label ?? o.name ?? String(o.value ?? o.id ?? o)));
  const formatDateShort = (val) => {
    if (!val) return '';
    try {
      const d = dayjs(val);
      if (!d.isValid()) return String(val).slice(0, 10);
      return d.format('DD-MM-YYYY');
    } catch {
      return String(val).slice(0, 10);
    }
  };

  const handleOpenDetail = async (item) => {
    const label = item.license_name || item.join?.driver_license_license_name || t('driverLicense.licenseDetail', 'License Detail');
    setDetailOpen(true);
    setDetailContent((prev) => ({ ...prev, label, front: '', back: '', document: '', type: 'photo' }));

    if (item.id) {
      try {
        const [frontRes, backRes, docRes] = await Promise.allSettled([
          getDriverLicenseDetailFile(item.id, 'front'),
          getDriverLicenseDetailFile(item.id, 'back'),
          getDriverLicenseDetailFile(item.id, 'document')
        ]);

        const front = frontRes.status === 'fulfilled' ? frontRes.value : '';
        const back = backRes.status === 'fulfilled' ? backRes.value : '';
        const doc = docRes.status === 'fulfilled' ? docRes.value : '';

        setDetailContent({
          label,
          front,
          back,
          document: doc,
          // Tentukan tipe tampilan berdasarkan file yang ada
          type: front || back ? 'photo' : doc ? 'document' : 'photo'
        });
      } catch (err) {
        console.error('Preview failed', err);
      }
    }
  };
  const handleDownloadFile = async (item) => {
    let fileUrl = item.url_license;
    if (!fileUrl && item.id) {
      try {
        fileUrl = await getDriverLicenseDetailFile(item.id, 'document');
      } catch (e) {
        console.error('Failed to get download url', e);
      }
    }

    if (fileUrl) {
      window.open(fileUrl, '_blank');
    } else {
      alert(t('driverLicense.noDocumentAvailable', 'No document file available to download.'));
    }
  };
  const [photoPreview, setPhotoPreview] = useState(null);
  useEffect(() => {
    const currentData = data || {};
    const base64Val = currentData._image_base64;

    if (base64Val && typeof base64Val === 'string' && base64Val.startsWith('data:')) {
      setPhotoPreview(base64Val);
      return;
    }

    const validateExistingImage = async () => {
      const dId = currentData.id;
      if (!dId) {
        setPhotoPreview(null);
        return;
      }
      try {
        const companyId = localStorage.getItem('companyId');
        const token = localStorage.getItem('apiKey');
        const timeNow = new Date().getTime();
        const baseURL = fleetApiGo?.defaults?.baseURL || '';
        const photoPath = `/images/driver/${companyId}/${dId}.png?apikey=${token}&t=${timeNow}`;
        setPhotoPreview(`${baseURL}${photoPath}`);
      } catch (err) {
        console.error('Error setting image url', err);
      }
    };
    validateExistingImage();
  }, [data]);
  const readOnlyFormik = {
    values: {
      ...data,
      license_file: licenseUrl ?? '',
      birth_date: data?.birth_date ? dayjs(data.birth_date).format('YYYY-MM-DD') : '',
      hired_date: data?.hired_date ? dayjs(data.hired_date).format('YYYY-MM-DD') : '',
      license_exp_date: data?.license_exp_date ? dayjs(data.license_exp_date).format('YYYY-MM-DD') : ''
    }
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography variant="h4">{t('masterDriver.master', 'Master Driver')}</Typography>
        </Box>
      </Box>

      {/* --- DATA UTAMA DRIVER --- */}
      <Box sx={{ width: '100%', maxWidth: 'none', px: { xs: 2, md: 3 }, py: { xs: 2, md: 3 } }}>
        <Box sx={{ display: 'flex', gap: 16, alignItems: 'stretch', width: '100%' }}>
          <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 16 }}>
            <Stack spacing={3}>
              <TextField
                label={t('masterDriver.driverName', 'Driver Name')}
                value={readOnlyFormik.values.name ?? ''}
                InputProps={{ readOnly: true }}
                fullWidth
                sx={readOnlyFieldStyle}
              />
              <TextField
                label="NIK"
                value={readOnlyFormik.values.ktp_no ?? ''}
                InputProps={{ readOnly: true }}
                fullWidth
                sx={readOnlyFieldStyle}
              />
              <TextField
                label={t('masterUser.phone', 'Phone')}
                value={readOnlyFormik.values.mobile_no ?? ''}
                InputProps={{ readOnly: true }}
                fullWidth
                sx={readOnlyFieldStyle}
              />
              <TextField
                label="Email"
                value={readOnlyFormik.values.email ?? ''}
                InputProps={{ readOnly: true }}
                fullWidth
                sx={readOnlyFieldStyle}
              />
              <TextField
                label={t('masterDriver.birthDate', 'Birth Date')}
                value={readOnlyFormik.values.birth_date ?? ''}
                InputProps={{ readOnly: true }}
                fullWidth
                sx={readOnlyFieldStyle}
              />
              <TextField
                label={t('masterDriver.birthPlace', 'Birth Place')}
                value={readOnlyFormik.values.birth_place ?? ''}
                InputProps={{ readOnly: true }}
                fullWidth
                sx={readOnlyFieldStyle}
              />
            </Stack>
          </Box>

          <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 16 }}>
            <Stack spacing={3}>
              <PickerField
                label={t('general.company', 'Company')}
                value={readOnlyFormik.values.company}
                options={companyOptions}
                onChange={() => {}}
                disabled={true}
                allowAdd={false}
                getOptionId={getOptionId}
                getOptionLabel={getOptionLabel}
                style={readOnlyFieldStyle}
              />
              <TextField
                label={t('masterDriver.code', 'Driver Code')}
                value={readOnlyFormik.values.driver_code ?? ''}
                InputProps={{ readOnly: true }}
                fullWidth
                sx={readOnlyFieldStyle}
              />
              <TextField
                label={t('masterDriver.driverType', 'Driver Type')}
                value={readOnlyFormik.values.driver_type ?? ''}
                InputProps={{ readOnly: true }}
                fullWidth
                sx={readOnlyFieldStyle}
              />
              <TextField
                label={t('masterDriver.hiredDate', 'Hired Date')}
                value={readOnlyFormik.values.hired_date ?? ''}
                InputProps={{ readOnly: true }}
                fullWidth
                sx={readOnlyFieldStyle}
              />

              <Box display="flex" gap={2} alignItems="center">
                <Box>
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    {t('masterDriver.driverPhoto', 'Driver Photo')}
                  </Typography>
                  <Box component="img" src={photoPreview} sx={{ width: 120, height: 120 }} />
                </Box>
              </Box>

              <Box sx={{ display: 'flex', gap: 3, alignItems: 'center', flexWrap: 'wrap' }}>
                <Box sx={{ alignItems: 'left' }}>
                  <Typography variant="body2">{t('general.status', 'Status')}</Typography>
                  <Switch checked={Boolean(readOnlyFormik.values.status)} disabled sx={activeSwitchStyle} />
                </Box>
                <Box>
                  <Typography variant="body2">{t('masterDriver.activePOD', 'Active POD')}</Typography>
                  <Switch checked={Boolean(readOnlyFormik.values.is_pod_active)} disabled sx={activeSwitchStyle} />
                </Box>
              </Box>
            </Stack>
          </Box>
        </Box>
      </Box>

      {/* --- PEMBATAS --- */}
      <Divider sx={{ my: 1 }} />

      {/* --- BAGIAN LICENSE DRIVER --- */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography variant="h4">Detail {t('masterDriver.driverLicense', 'Driver License')}</Typography>
        </Box>
      </Box>
      <Box sx={{ width: '100%', maxWidth: 'none' }}>
        {Array.isArray(data.driver_license) && data.driver_license.length > 0 ? (
          <Stack spacing={2}>
            {data.driver_license.map((item, idx) => {
              const label = item.join?.driver_license_license_name || item.license_type_label || item.license_type || t('general.unknown');
              const expFormatted = item.license_exp_date ? formatDateShort(item.license_exp_date) : 'No Exp';

              return (
                <Paper
                  key={`lic-${idx}`}
                  variant="outlined"
                  sx={{
                    borderRadius: 2,
                    overflow: 'hidden',
                    bgcolor: 'background.paper'
                  }}
                >
                  {/* Info Text */}
                  <Grid container spacing={16} padding={2}>
                    <Grid size={{ xs: 6, md: 6 }}>
                      <TextField
                        label={t('driverLicense.licenseType', 'License Type')}
                        value={label}
                        InputProps={{ readOnly: true }}
                        fullWidth
                        sx={readOnlyFieldStyle}
                      />
                    </Grid>
                    <Grid size={{ xs: 6, md: 6 }}>
                      <TextField
                        label={t('driverLicense.expiredDate', 'Expired Date')}
                        value={expFormatted}
                        InputProps={{ readOnly: true }}
                        fullWidth
                        sx={readOnlyFieldStyle}
                      />
                    </Grid>
                  </Grid>
                  <Grid container spacing={16} padding={2}>
                    <Grid size={{ xs: 6, md: 6 }}>
                      <TextField
                        label={t('driverLicense.licenseNo', 'License No')}
                        value={item.license_no || '—'}
                        InputProps={{ readOnly: true }}
                        fullWidth
                        sx={readOnlyFieldStyle}
                      />
                    </Grid>
                    <Grid container spacing={2} size={{ xs: 6, md: 6 }}>
                      <Grid>
                        <Box
                          sx={{
                            width: 40,
                            height: 40,
                            border: '3px solid',
                            borderColor: 'primary.main',
                            borderRadius: 2,
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                            cursor: 'pointer',
                            color: 'primary.main',
                            '&:hover': {
                              bgcolor: 'primary.50'
                            }
                          }}
                          onClick={() => handleDownloadFile(item)}
                          title={t('driverLicense.downloadDocument', 'Download Document')}
                        >
                          <DownloadIcon />
                        </Box>
                      </Grid>
                      <Grid>
                        <Box
                          onClick={() => handleOpenDetail(item)}
                          sx={{
                            height: 40,
                            p: 1,
                            borderRadius: 2,
                            overflow: 'hidden',
                            flexShrink: 0,
                            display: 'flex',
                            gap: 2,
                            justifyContent: 'center',
                            alignItems: 'center',
                            border: '1px solid',
                            borderColor: 'primary.main',
                            cursor: 'pointer'
                          }}
                        >
                          <Typography>{t('masterDriver.viewDocumentDriverLicense', 'View Document Driver License')}</Typography>
                        </Box>
                      </Grid>
                    </Grid>
                  </Grid>
                </Paper>
              );
            })}
          </Stack>
        ) : (
          <Box sx={{ py: 4, textAlign: 'center' }}>
            <Typography variant="body1" color="text.secondary" sx={{ fontStyle: 'italic' }}>
              {t('masterDriver.noLicenseData', 'No license data available.')}
            </Typography>
          </Box>
        )}
      </Box>
      <DetailDialog open={detailOpen} onClose={() => setDetailOpen(false)} title={detailContent.label} maxWidth="sm">
        <Box>
          {detailContent.type === 'photo' ? (
            <Grid container spacing={3} justifyContent="center" alignItems="flex-start">
              <Grid item xs={6}>
                <Typography variant="caption" sx={{ fontWeight: 'bold', display: 'block', color: 'text.secondary', textAlign: 'center' }}>
                  {t('driverLicense.frontPhoto', 'FRONT PHOTO')}
                </Typography>
                <Box
                  sx={{
                    height: 180,
                    maxWidth: 300,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    bgcolor: '#f5f5f5',
                    overflow: 'hidden',
                    border: '1px dashed #ccc',
                    borderRadius: 1,
                    mt: 1
                  }}
                >
                  {detailContent.front ? (
                    <Box
                      component="img"
                      src={detailContent.front}
                      alt="Front"
                      sx={{ width: '100%', height: '100%', objectFit: 'contain' }}
                    />
                  ) : (
                    <Stack alignItems="center" spacing={1}>
                      <InsertPhotoOutlinedIcon sx={{ fontSize: 40, color: 'grey.400' }} />
                      <Typography variant="caption" color="text.disabled">
                        {t('driverLicense.noImage', 'No Image')}
                      </Typography>
                    </Stack>
                  )}
                </Box>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="caption" sx={{ fontWeight: 'bold', display: 'block', color: 'text.secondary', textAlign: 'center' }}>
                  {t('driverLicense.backPhoto', 'BACK PHOTO')}
                </Typography>
                <Box
                  sx={{
                    height: 180,
                    maxWidth: 300,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    bgcolor: '#f5f5f5',
                    overflow: 'hidden',
                    border: '1px dashed #ccc',
                    borderRadius: 1,
                    mt: 1
                  }}
                >
                  {detailContent.back ? (
                    <Box component="img" src={detailContent.back} alt="Back" sx={{ width: '100%', height: '100%', objectFit: 'contain' }} />
                  ) : (
                    <Stack alignItems="center" spacing={1}>
                      <InsertPhotoOutlinedIcon sx={{ fontSize: 40, color: 'grey.400' }} />
                      <Typography variant="caption" color="text.disabled">
                        {t('driverLicense.noImage', 'No Image')}
                      </Typography>
                    </Stack>
                  )}
                </Box>
              </Grid>
            </Grid>
          ) : (
            <Box sx={{ textAlign: 'center' }}>
              <Paper
                variant="outlined"
                sx={{ height: 300, borderRadius: 2, p: 2, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}
              >
                <Box sx={{ width: '100%', height: '100%', bgcolor: '#eee' }}>
                  <iframe
                    width="100%"
                    height="100%"
                    src={`https://docs.google.com/gview?url=${detailContent.document}&embedded=true`}
                    style={{ border: 'none' }}
                    title="Document Preview"
                  />
                </Box>
                <Button
                  startIcon={<CloudDownload />}
                  variant="contained"
                  size="small"
                  component="a"
                  href={detailContent.document}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {t('driverLicense.downloadFile', 'Download File')}
                </Button>
              </Paper>
            </Box>
          )}
        </Box>
      </DetailDialog>
    </Box>
  );
}

MasterDriverDetail.propTypes = {
  driver: PropTypes.object,
  id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onClose: PropTypes.func
};
